import { jsx as _jsx } from "react/jsx-runtime";
import { Box, freddyTheme, Link, Typography } from '@coolblue-development/freddy';
import clickFeedbackTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/clickFeedbackTrigger';
import clickLinkTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/clickLinkTrigger';
import clickSendFeedbackTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/clickSendFeedbackTrigger';
import selectOpenAnswerTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/selectOpenAnswerTrigger';
import selectProductTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/selectProductTrigger';
import { Charger } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { MarkdownText, Message } from '@coolblue-development/next-chat/client';
import { UiMessageTypes } from '@coolblue-development/next-chat/types';
import { ThumbInput } from '@coolblue-development/next-feedback/client';
import { ConditionalWrapper } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { AddToCartButton } from '@coolblue-development/next-shopping-cart/client';
import { useCallback, useState } from 'react';
import { useTranslation } from '../../../../i18n/useTranslation.js';
import { addProductToCart } from '../../../../server/index.js';
import { ThinkingLoader } from '../ThinkingLoader/ThinkingLoader.js';
export function Conversation({ loader, messages, delayMessageRender = undefined, imageConfig, shouldAnimateMessage = false, }) {
    const { trackEvent } = useAnalytics();
    const { device } = useRequestContext();
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation('adviceAgent');
    const handleClick = (e) => {
        if (e.target instanceof HTMLAnchorElement) {
            trackEvent(clickLinkTrigger({
                url: e.target.href,
                redirect: e.target.text,
                sub_feature: 'product_link',
            }));
        }
    };
    const handleProductAdviceLinkClick = useCallback((index, totalIndices, productId) => {
        trackEvent(selectProductTrigger({
            index,
            total_indices: totalIndices,
            method: 'link',
            item_id: productId,
        }));
    }, [trackEvent]);
    const handleAddToCart = async (productId) => {
        setIsLoading(true);
        const addToCartPageUrl = await addProductToCart(productId.toString());
        if (addToCartPageUrl) {
            window.location.assign(addToCartPageUrl);
            return;
        }
        setIsLoading(false);
    };
    const renderMessage = (message, index) => {
        const isLatestMessage = index === messages.length - 1;
        const renderFeedbackElement = () => {
            if (isLatestMessage && index !== 0 && !message.isUser && message.id) {
                return (_jsx(ThumbInput, { referrer: `msg_${message.id}`, labels: [], tracking: {
                        inputFocusEvent: selectOpenAnswerTrigger(),
                        submitButtonClickEvent: clickSendFeedbackTrigger(),
                        thumbClickVoteDownEvent: clickFeedbackTrigger({
                            feedback: 'vote_down',
                            unique_id: `msg_${message.id}`,
                        }),
                        thumbClickVoteUpEvent: clickFeedbackTrigger({
                            feedback: 'vote_up',
                            unique_id: `msg_${message.id}`,
                        }),
                    }, variant: "subtle", justifyContent: "flex-end", inputActiveFontSize: device === 'mobile' ? '16px' : undefined }));
            }
            return null;
        };
        switch (message.type) {
            case UiMessageTypes.Message:
                if (!message.text) {
                    return null;
                }
                return (_jsx(Box, { mt: index === 0 ? 0 : 200, sx: theme => shouldAnimateMessage
                        ? {
                            animation: `${theme.motionAnimation.fadeIn} ${theme.motionDuration.slower} ${theme.motionEasing.easeOut} forwards`,
                        }
                        : {}, children: _jsx(Message, { variant: message.isUser ? 'user' : 'system', hideUsername: true, showTimestamp: !!(message.isUser || message.id), timeInUtcMs: message.time, feedbackElement: renderFeedbackElement(), maxWidth: "100%", children: _jsx(ConditionalWrapper, { condition: !message.isUser, wrapper: children => (_jsx(MarkdownText, { text: children, customComponents: {
                                    'product-advice-link': ({ href, children, index, totalIndices, productId, variant, }) => {
                                        return (_jsx(ConditionalWrapper, { condition: variant !== undefined, wrapper: children => (_jsx(Typography, { variant: variant, children: children })), children: _jsx(Link, { href: href, target: "_blank", onClick: () => handleProductAdviceLinkClick(index, totalIndices, productId), children: children }) }));
                                    },
                                    'atc-button': ({ productId }) => {
                                        return (_jsx(AddToCartButton, { isFullWidth: true, label: t('addToCartButton.label'), onClick: () => handleAddToCart(productId), loading: isLoading }));
                                    },
                                }, customIconMap: {
                                    charger: _jsx(Charger, { fill: freddyTheme.color.icon.subtle }),
                                }, imageConfig: imageConfig, onClick: handleClick })), children: message.text }) }) }, index));
            case UiMessageTypes.Loading:
                return (_jsx(Box, { my: 200, children: _jsx(ThinkingLoader, { video: { sources: loader.videoSources } }) }, index));
            default:
                return null;
        }
    };
    return messages.map((message, index) => {
        if (delayMessageRender && index > delayMessageRender.renderUpToIndex) {
            return null;
        }
        return renderMessage(message, index);
    });
}
//# sourceMappingURL=Conversation.js.map